import React, { useCallback, useEffect, useMemo } from 'react';
import { DialogProps } from '@mui/material/Dialog';
import EmailVerifyForm from '../EmailVerifyForm';
import TwoFAForm from '../TwoFAForm';
import { LoginResponse } from '../../../../apis/user/types';
import { useTranslation } from 'react-i18next';
import { ZigTypography } from '@zignaly-open/ui';
import { Container, Title } from './styles';
import {
  useResendKnownDeviceCode,
  useVerify2FA,
  useResendCodeEnableUser,
  useVerifyEmail,
  useVerifyEmailKnownDevice,
  useVerifyEmailNewUser,
  useResendCodeNewUser,
} from '../../../../apis/user/use';
import { useToast } from '../../../../util/hooks/useToast';
import ZModal from '../../../../components/ZModal';
import useTrackEvent from '../../../../components/Navigation/Tracker/use';
import { gtagEvent } from '../../../../util/analytics';

function AuthVerifyModal({
  user,
  close,
  onSuccess,
  onFailure,
  ...props
}: {
  user: { token: string } & Partial<LoginResponse>;
  close: () => void;
  onSuccess: () => void;
  onFailure: ({ message }: { message: string }) => void;
} & DialogProps): React.ReactElement {
  const { t } = useTranslation(['auth', 'error']);
  const { ask2FA, disabled, emailUnconfirmed, isUnknownDevice } = user;
  const resendEmail = useResendCodeEnableUser();
  const resendEmailNewUser = useResendCodeNewUser();
  const trackEvent = useTrackEvent();
  const resendDevice = useResendKnownDeviceCode();
  const verifyEmail = useVerifyEmail();
  const verifyEmailNewUser = useVerifyEmailNewUser();
  const verifyDevice = useVerifyEmailKnownDevice();
  const [submit2FA, status2FA] = useVerify2FA();
  const toast = useToast();

  let [verify, verifyStatus] = verifyEmailNewUser;
  let [resend, resendStatus] = resendEmailNewUser;

  if (!emailUnconfirmed && isUnknownDevice) {
    [verify, verifyStatus] = verifyDevice;
    [resend, resendStatus] = resendDevice;
  } else if (disabled) {
    [verify, verifyStatus] = verifyEmail;
    [resend, resendStatus] = resendEmail;
  }

  const performResend = () => {
    resend().then(() => toast.success(t('auth:resend-code')));
    gtagEvent('verification_resend_code');
  };

  const texts = useMemo(() => {
    let title = '';
    let description = '';
    if (disabled) {
      if (!verifyStatus.isSuccess) {
        title = t('auth-verify-modal.isDisabled.isNotVerifyEmailValid.title');
        description = t(
          'auth-verify-modal.isDisabled.isNotVerifyEmailValid.description',
        );
      } else {
        title = t('auth-verify-modal.isNotDisabled.ask2FA.twoFA-title');
      }
    } else if (ask2FA) {
      if (verifyStatus.isSuccess) {
        title = t('auth-verify-modal.isNotDisabled.ask2FA.twoFA-title');
      } else if (isUnknownDevice) {
        title = t(
          'auth-verify-modal.isNotDisabled.ask2FA.isUnknownDevice-title',
        );
      } else {
        title = t('auth-verify-modal.isNotDisabled.ask2FA.twoFA-title');
      }
    } else {
      if (emailUnconfirmed) {
        title = t(
          'auth-verify-modal.isNotDisabled.askNot2FA.isEmailUnconfirmed.title',
        );
        description = t(
          'auth-verify-modal.isNotDisabled.askNot2FA.isEmailUnconfirmed.description',
        );
      } else if (isUnknownDevice) {
        title = t(
          'auth-verify-modal.isNotDisabled.askNot2FA.isUnknownDevice-title',
        );
      }
    }
    return { title, description };
  }, [user, t, verifyStatus.isSuccess]);

  const getError = useCallback(
    (status: typeof verifyStatus) => {
      const errorCode = (
        status?.error as { data?: { error: { code: number } } }
      )?.data?.error?.code;

      return errorCode === 13
        ? t('error:error.login-session-expired')
        : [37, 108, 1086].includes(errorCode)
          ? t(`error:error.${errorCode}`)
          : null;
    },
    [t],
  );

  const onClickClose = () => {
    onFailure({ message: t('error:error.failed-verification') });
    close();
  };

  const askEmailCode = isUnknownDevice || disabled || emailUnconfirmed;
  const allGood =
    (!ask2FA || status2FA.isSuccess) &&
    (!askEmailCode || verifyStatus.isSuccess);

  useEffect(() => {
    if (allGood) {
      trackEvent('verify-success');
      onSuccess();
      close();
    }
  }, [allGood]);

  return (
    <ZModal
      wide
      allowUnauth
      disableBackdropClose
      {...props}
      close={emailUnconfirmed ? null : onClickClose}
      title={texts.title}
      titleAlign='center'
      mobileFullScreen
    >
      <Title data-testid={'auth-verify-modal__title'}>
        {texts.description && (
          <ZigTypography
            whiteSpace='pre-line'
            id={'auth-verify-modal__description'}
            textAlign={'center'}
          >
            {texts.description}
          </ZigTypography>
        )}
      </Title>
      <Container
        isSeveralCodes={
          askEmailCode &&
          !verifyStatus.isSuccess &&
          ask2FA &&
          !status2FA.isSuccess
        }
      >
        {askEmailCode && !verifyStatus.isSuccess && (
          <EmailVerifyForm
            clearOnError
            onSubmit={(code) =>
              verify({ code })
                .unwrap()
                .catch(() => gtagEvent('verification_code_invalid'))
            }
            onReSendCode={performResend}
            error={getError(verifyStatus)}
            isReSendLoading={resendStatus.isLoading}
            isLoading={verifyStatus.isLoading}
            prefixId='new-device-modal'
            // auto focus initially
            // force setting it to false if 2fa is being filled to be able to re-focus after 2fa passed
            autoFocus={
              !ask2FA || status2FA.isUninitialized || status2FA.isSuccess
            }
          />
        )}

        {ask2FA && !status2FA.isSuccess && (
          <TwoFAForm
            clearOnError
            onSubmit={(code) => submit2FA({ code })}
            isLoading={status2FA.isLoading}
            error={getError(status2FA)}
            // auto focus initially if no email code and when email code has passed
            autoFocus={!askEmailCode || verifyStatus.isSuccess}
          />
        )}
      </Container>
    </ZModal>
  );
}

AuthVerifyModal.trackId = '2fa-verify';

export default AuthVerifyModal;
